import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import {
  createScoreLinkApi,
  type ScoreLinkApi,
  type ScoreLinkBoard,
  type ScoreLinkSnapshot,
} from './scoreboardApi';
import { ScoreEntryBoard } from './ScoreEntryBoard';
import { lastEntryDay, rawFields, valuesFromDraft } from './scoreboard';
import './scoreEntry.css';

export function ScoreEntryPage() {
  const { token = '' } = useParams();
  const { client } = useAuth();
  const api = useMemo(() => createScoreLinkApi(client), [client]);
  return <ScoreEntryScreen key={token} token={token} api={api} />;
}
export function ScoreEntryScreen({ token, api }: { token: string; api: ScoreLinkApi }) {
  const max = lastEntryDay();
  const days = Array.from({ length: 7 }, (_, i) =>
    new Date(Date.parse(`${max}T00:00:00Z`) - i * 86400000).toISOString().slice(0, 10),
  );
  const [date, setDate] = useState(max);
  const [snapshot, setSnapshot] = useState<ScoreLinkSnapshot | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false),
    [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true),
    [saved, setSaved] = useState(false);
  const [error, setError] = useState(''),
    [conflict, setConflict] = useState(false);
  const [revision, setRevision] = useState(0);
  const [board, setBoard] = useState<ScoreLinkBoard | null>(null);
  const [boardFailed, setBoardFailed] = useState(false);
  const [boardRevision, setBoardRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setBoard(null);
    setBoardFailed(false);
    api
      .board(token, date)
      .then((value) => {
        if (active) setBoard(value);
      })
      .catch(() => {
        if (active) setBoardFailed(true);
      });
    return () => {
      active = false;
    };
  }, [api, token, date, revision, boardRevision]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setSnapshot(null);
    setError('');
    setSaved(false);
    setConflict(false);
    api
      .load(token, date)
      .then((value) => {
        if (!active) return;
        setSnapshot(value);
        setDraft(
          Object.fromEntries(
            value.metrics.map((m) => [m.id, m.value == null ? '' : String(m.value)]),
          ),
        );
        setDirty(false);
      })
      .catch(() => {
        if (active) setError('This entry link is unavailable. Ask the 4ocean team for a new link.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, token, date, revision]);
  useEffect(() => {
    if (!dirty && !busy) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, busy]);
  function discard(action: () => void) {
    if (!dirty || window.confirm('Discard your unsaved numbers?')) action();
  }
  const fields = snapshot ? rawFields(snapshot.metrics) : [];
  const groups = new Map<string, typeof fields>();
  for (const field of fields) {
    const index = field.label.lastIndexOf(' · ');
    const group = index < 0 ? 'Totals' : field.label.slice(0, index);
    groups.set(group, [...(groups.get(group) ?? []), field]);
  }
  return (
    <main className="score-entry-page">
      <div className="score-entry-shell score-entry-wide">
        <p className="score-entry-eyebrow">4ocean · LinkedIn ads numbers</p>
        <h1>Enter {date === max ? "yesterday's" : 'the selected day’s'} LinkedIn ads numbers</h1>
        <p>For {date}. Copy them from Campaign Manager. Leave a box blank if you don't have it.</p>
        <div className="score-entry-layout">
          <div className="score-entry-board-column">
            {board ? <ScoreEntryBoard board={board} openSection={snapshot?.section_name} /> : null}
            {boardFailed && !error ? (
              <p className="score-entry-board-note">The scoreboard could not load right now.</p>
            ) : null}
          </div>
          <div className="score-entry-form-column">
            {loading ? <p role="status">Loading entry form…</p> : null}
            {error ? <p role="alert">{error}</p> : null}
            {conflict ? (
              <button onClick={() => discard(() => setRevision((n) => n + 1))}>
                Reload saved version
              </button>
            ) : null}
            {saved ? (
              <section className="score-entry-block">
                <h2>Saved. Thank you.</h2>
                <p>Come back to this same link tomorrow for the next day.</p>
                <button onClick={() => setSaved(false)}>Edit what I entered</button>
              </section>
            ) : null}
            {snapshot && !saved ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (busy || conflict) return;
                  setBusy(true);
                  setError('');
                  void (async () => {
                    try {
                      const version = await api.save(
                        token,
                        snapshot,
                        valuesFromDraft(snapshot.metrics, draft),
                      );
                      setSnapshot({ ...snapshot, version });
                      setDirty(false);
                      setSaved(true);
                      setBoardRevision((n) => n + 1);
                    } catch (cause) {
                      const code =
                        typeof cause === 'object' && cause !== null && 'code' in cause
                          ? cause.code
                          : null;
                      if (code === '40001') {
                        setConflict(true);
                        setError(
                          'Someone changed these numbers or fields. Your draft is kept. Reload and reconcile before saving.',
                        );
                      } else if (code === '42501') {
                        setConflict(true);
                        setError(
                          'This entry link is unavailable. Ask the 4ocean team for a new link.',
                        );
                      } else
                        setError(
                          cause instanceof Error
                            ? cause.message
                            : 'Could not save. Your draft is kept. Please try again.',
                        );
                    } finally {
                      setBusy(false);
                    }
                  })();
                }}
              >
                <fieldset disabled={busy}>
                  <legend className="ui-visually-hidden">Ads numbers</legend>
                  <label>
                    Manila day
                    <select
                      value={date}
                      onChange={(event) => discard(() => setDate(event.target.value))}
                    >
                      {days.map((day) => (
                        <option key={day}>{day}</option>
                      ))}
                    </select>
                  </label>
                  {[...groups].map(([name, metrics]) => (
                    <section className="score-entry-block" key={name}>
                      <h2>{name}</h2>
                      <div className="score-entry-grid">
                        {metrics.map((field) => (
                          <label key={field.id}>
                            {name === 'Totals' ? field.label : field.label.slice(name.length + 3)}
                            {field.value_type === 'money' ? ' (USD)' : ''}
                            <input
                              type="number"
                              min="0"
                              step={field.value_type === 'count' ? '1' : 'any'}
                              inputMode={field.value_type === 'count' ? 'numeric' : 'decimal'}
                              value={draft[field.id] ?? ''}
                              onChange={(event) => {
                                setDraft({ ...draft, [field.id]: event.target.value });
                                setDirty(true);
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    </section>
                  ))}
                  <p>
                    These numbers go straight to the 4ocean team. You can see the scoreboard and
                    enter the LinkedIn ads numbers here, nothing else.
                  </p>
                  <button type="submit" disabled={conflict || fields.length === 0}>
                    {busy ? 'Saving…' : 'Save numbers'}
                  </button>
                </fieldset>
              </form>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
}
